import { Table, TableBody, TableContainer, TableRow } from "@mui/material";
import Paper from "@mui/material/Paper";
import TableCell from "@mui/material/TableCell";
import React, { useContext } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CustomEvent, SpontaneousEvent } from "../../../../interface/CustomEvent";
import { Task, TaskStatus } from "../../../../interface/Task";
import { Timestamp } from "../../../../interface/Timestamp";
import { GameContext } from "../../../../contexts/GameContext";
import { addMinutes, displayTime } from "../../../../interface/Time";
import CustomTooltip from "./ScoresToolTip";
import { PopupContainer } from "../../../Popup/styled";


interface Props {
    roundScores: Timestamp[]
    average: number,
    finalScore: number,
    tasks: Task[],
    events: CustomEvent[]
}


const RoundResultPopup = ({ roundScores, average, finalScore, tasks, events }: Props) => {
    const { round, beginTime, stepDuration } = useContext(GameContext);

    function getData() {
        const res: { x: string; value: number; }[] = [];
        let newDay = false;
        roundScores.forEach((val, key) => {
            const time = addMinutes(beginTime[round], key * stepDuration);
            if (time.hours >= 24) { time.hours -= 24 }

            let strTime = displayTime(time);
            if (JSON.stringify(time) === JSON.stringify(beginTime[round])) {
                if (newDay) {
                    strTime = "*" + strTime;
                }
                newDay = true
            }

            res.push({
                x: strTime,
                value: +val.score.toFixed(1)
            });


        });
        return res;
    }

    function getTicks() {
        const ticks: string[] = [];
        let newDay = false;
        roundScores.forEach((t, i) => {
            const targetTime = addMinutes(beginTime[round], i * stepDuration);
            if (targetTime.hours >= 24) {
                targetTime.hours -= 24;
            }

            let strTime = displayTime(targetTime);

            if (JSON.stringify(targetTime) === JSON.stringify(beginTime[round])) {
                if (newDay) {
                    strTime = "*" + strTime;
                }
                newDay = true
            }

            if (targetTime.minutes == 0) {
                ticks.push(strTime);
            }

        });
        return ticks
    }

    return (
        <PopupContainer>
            <div style={{ "width": "100%", "marginBottom": "1em" }}>
                <div style={{ "marginBottom": "1em", "marginRight": "1em" }}>
                    <ResponsiveContainer width={"100%"} height={400}>
                        <LineChart
                            width={500}
                            height={400}
                            data={getData()}
                            margin={{
                                top: 20,
                                right: 20,
                                left: 20,
                                bottom: 20,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="x" minTickGap={0} ticks={getTicks()} label={{ value: 'Score', position: 'insideBottom', offset: -10, style: { fontWeight: "bold" } }} />
                            <YAxis dataKey="value" />
                            <Tooltip content={<CustomTooltip />} />
                            <Line name="Score" type="monotone" dot={false} dataKey="value" stroke="#8884d8" activeDot={{ r: 8 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>


                <div style={{ "width": "fit-content", "margin": "auto" }}>
                    <TableContainer component={Paper} style={{ "overflowX": "visible", "marginBottom": "1em" }}>
                        <Table sx={{ minWidth: 500, tabSize: 4 }} aria-label="simple table" size="small">
                            <TableBody>

                                <TableRow>
                                    <TableCell style={{ "fontWeight": "bold" }}>Moyenne cumulative</TableCell>
                                    <TableCell style={{ "fontWeight": "bold" }}>{average}</TableCell>
                                </TableRow>

                                <TableRow><TableCell colSpan={2} style={{ "fontWeight": "bold" }}>Tâches</TableCell></TableRow>

                                {tasks.map((task, id) => (
                                    <TableRow key={id}>
                                        <TableCell style={{ "paddingLeft": "4em" }}>{task.name}</TableCell>
                                        <TableCell>{task.status === TaskStatus.DONE ? "✅" : `-${task.penalty}`}</TableCell>
                                    </TableRow>
                                ))}

                                <TableRow><TableCell colSpan={2} style={{ "fontWeight": "bold" }}>Événements</TableCell></TableRow>

                                {events.map((e, eventId) => (
                                    <React.Fragment key={eventId}>
                                        <TableRow key={eventId}>
                                            <TableCell colSpan={2} style={{ "fontStyle": "italic" }}>{e.name}</TableCell>
                                        </TableRow>
                                        {e.tasks.map((t, taskId) => (
                                            <TableRow key={taskId}>
                                                <TableCell style={{ "paddingLeft": "4em" }}>{t.name}</TableCell>
                                                <TableCell>{t.status === TaskStatus.DONE ? "✅" : `-${t.penalty}`}</TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}

                                <TableRow>
                                    <TableCell style={{ "fontWeight": "bold" }}>Total</TableCell>
                                    <TableCell style={{ "fontWeight": "bold" }}>{finalScore}</TableCell>
                                </TableRow>

                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

            </div>

        </PopupContainer>


    );
};


export default RoundResultPopup;